import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { isNil } from 'lodash';

import './EmbeddedScrollable.scss';

/**
 * Utility component which allows scrollable components to be dynamically
 * resized by layout components like Row and Column.
 *
 * This component is meant to be used with "fill" sizing options on the outer
 * component.
 *
 * @param props The elements to display inside the scrollable area
 */
export const EmbeddedScrollable: React.FC<PropsWithChildren> = (props) => {
    const ref = useRef(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<string | undefined>();
    const [height, setHeight] = useState<string | undefined>();
    const [isScrolling, setIsScrolling] = useState(false);

    // Show scrollbar when scrolling
    useEffect(() => {
        const scrollElement = scrollRef.current;
        if (!scrollElement) return;

        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            setIsScrolling(true);
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                setIsScrolling(false);
            }, 1000);
        };

        scrollElement.addEventListener('scroll', handleScroll);
        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, []);

    // hack to scroll inside a dynamically sized element
    // this works by using an outer element to find the target size
    // then setting an inner element's absolute size to the target
    useEffect(() => {
        if (!isNil(ref.current)) {
            if (typeof ResizeObserver !== 'function') {
                // this means the class is not defined, we are probably running inside nodejs or something
                // this class may be getting rendered as part of a test of a different component
                // set the width and height to something safe so we don't break the test
                setWidth('100%');
                setHeight('100%');
                return;
            }

            const observer = new ResizeObserver((entries) => {
                entries.forEach((entry, idx) => {
                    setHeight(`${entry.contentRect.height}px`);
                    setWidth(`${entry.contentRect.width}px`);
                });
            });

            observer.observe(ref.current);

            return () => observer.disconnect();
        }
    }, [ref.current]);

    return (
        <div style={{ width: '100%', height: '100%' }} ref={ref}>
            <div
                ref={scrollRef}
                style={{ width, height, overflow: 'scroll' }}
                className={`embedded-scrollable ${isScrolling ? 'scrolling' : ''}`}
            >
                {!isNil(width) && !isNil(height) && props.children}
            </div>
        </div>
    );
};
